// Toasts (docs/plans/toast.md): a title and an optional detail on a card at the
// top centre. The list itself is plain data in toast-queue.ts; this file draws
// it and runs the clocks. All motion is CSS (styles.css: toast-in, toast-out,
// toast-bar).
import { AlertCircle, AlertTriangle, CheckCircle2, Info, Loader2, X } from './icons.tsx'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { cx } from './cx.ts'
import {
  leave,
  lifetime,
  newest,
  push,
  remove,
  shown,
  update,
  type ToastAction,
  type ToastItem,
  type ToastOptions,
  type ToastTone,
} from './toast-queue.ts'

export type { ToastAction, ToastOptions } from './toast-queue.ts'

/** A slow job's toast: its words change in place, then it becomes the result. */
export interface ToastHandle {
  update(words: { title?: string; detail?: string }): void
  success(title: string, options?: ToastOptions): void
  error(title: string, options?: ToastOptions): void
  dismiss(): void
}

/** The second argument of a toast: its options, or (as before) just its button. */
type Extra = ToastOptions | ToastAction

interface ToastApi {
  success(title: string, extra?: Extra): void
  error(title: string, extra?: Extra): void
  warning(title: string, extra?: Extra): void
  info(title: string, extra?: Extra): void
  /** Work that takes more than a moment: one toast from start to result. */
  loading(title: string, options?: Pick<ToastOptions, 'detail'>): ToastHandle
}

const ToastContext = createContext<ToastApi | null>(null)

const optionsOf = (extra?: Extra): ToastOptions =>
  extra && 'label' in extra ? { action: extra } : (extra ?? {})

/** How long the exit plays before the toast is taken away (toast-out in styles.css). */
const EXIT_MS = 160

const LOOK: Record<ToastTone, { icon: typeof Info; tile: string; edge: string; bar: string }> = {
  success: {
    icon: CheckCircle2,
    tile: 'bg-success-light text-success',
    edge: 'bg-success',
    bar: 'bg-success',
  },
  error: {
    icon: AlertCircle,
    tile: 'bg-danger-light text-danger',
    edge: 'bg-danger',
    bar: 'bg-danger',
  },
  warning: {
    icon: AlertTriangle,
    tile: 'bg-warning-light text-warning',
    edge: 'bg-warning',
    bar: 'bg-warning',
  },
  info: { icon: Info, tile: 'bg-info-light text-info', edge: 'bg-info', bar: 'bg-info' },
  loading: { icon: Loader2, tile: 'bg-soft text-muted', edge: 'bg-muted', bar: 'bg-muted' },
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [list, setList] = useState<ToastItem[]>([])
  const nextId = useRef(1)

  const add = useCallback((tone: ToastTone, title: string, options: ToastOptions = {}): number => {
    const fresh = nextId.current++
    let id = fresh
    setList((all) => {
      const next = push(all, fresh, tone, title, options)
      id = next.id
      return next.list
    })
    return id
  }, [])
  const dismiss = useCallback((id: number) => setList((all) => leave(all, id)), [])
  const drop = useCallback((id: number) => setList((all) => remove(all, id)), [])

  const api = useMemo<ToastApi>(
    () => ({
      success: (t, e) => void add('success', t, optionsOf(e)),
      error: (t, e) => void add('error', t, optionsOf(e)),
      warning: (t, e) => void add('warning', t, optionsOf(e)),
      info: (t, e) => void add('info', t, optionsOf(e)),
      loading: (title, options) => {
        // A slow job is never merged into another toast, so this id is its own.
        const id = add('loading', title, options)
        const finish =
          (tone: ToastTone) =>
          (t: string, o: ToastOptions = {}) =>
            setList((all) =>
              update(all, id, { detail: undefined, action: undefined, ...o, tone, title: t }),
            )
        return {
          update: (words) => setList((all) => update(all, id, words)),
          success: finish('success'),
          error: finish('error'),
          dismiss: () => dismiss(id),
        }
      },
    }),
    [add, dismiss],
  )

  // Escape puts the newest toast away, unless a dialog is open (it closes that).
  const anyStaying = list.some((t) => !t.leaving)
  useEffect(() => {
    if (!anyStaying) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || document.querySelector('[role="dialog"]')) return
      setList((all) => {
        const top = newest(all)
        return top ? leave(all, top.id) : all
      })
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [anyStaying])

  const { visible, more } = shown(list)
  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        // Top centre keeps dialog footers and the checkout button clear; outside the
        // app root so an open dialog doesn't make "Undo" unclickable (see Modal).
        // No `status` role on a toast: screens use that role for their own notes.
        <div
          aria-live="polite"
          data-stay-interactive=""
          className="pointer-events-none fixed top-4 left-1/2 z-[60] flex w-[min(92vw,380px)] -translate-x-1/2 flex-col"
        >
          {visible.map((t) => (
            <ToastCard key={t.id} item={t} onDismiss={dismiss} onGone={drop} />
          ))}
          {more > 0 && (
            <span className="self-center rounded-full bg-soft px-2.5 py-0.5 text-xs font-semibold text-muted shadow-card">
              {more} more
            </span>
          )}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}

function ToastCard({
  item,
  onDismiss,
  onGone,
}: {
  item: ToastItem
  onDismiss: (id: number) => void
  onGone: (id: number) => void
}) {
  const look = LOOK[item.tone]
  const Icon = look.icon
  const life = lifetime(item)
  // Hovered or focused: the clock and the line wait.
  const [held, setHeld] = useState(false)
  const [drag, setDrag] = useState<{ x: number; y: number } | null>(null)
  const from = useRef<{ x: number; y: number } | null>(null)
  // What is left of the clock, kept across pauses; starts again with each stamp.
  const clock = useRef({ stamp: -1, left: 0 })

  useEffect(() => {
    if (life === null || item.leaving) return
    if (clock.current.stamp !== item.stamp) clock.current = { stamp: item.stamp, left: life }
    if (held) return
    const started = Date.now()
    const timer = setTimeout(() => onDismiss(item.id), clock.current.left)
    return () => {
      clearTimeout(timer)
      clock.current.left = Math.max(0, clock.current.left - (Date.now() - started))
    }
  }, [life, held, item.id, item.stamp, item.leaving, onDismiss])

  useEffect(() => {
    if (!item.leaving) return
    const timer = setTimeout(() => onGone(item.id), EXIT_MS)
    return () => clearTimeout(timer)
  }, [item.leaving, item.id, onGone])

  // A finger drags it away: up, or to either side.
  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType === 'mouse' || (e.target as HTMLElement).closest('button')) return
    from.current = { x: e.clientX, y: e.clientY }
  }
  const onPointerMove = (e: PointerEvent) => {
    if (!from.current) return
    setDrag({ x: e.clientX - from.current.x, y: Math.min(0, e.clientY - from.current.y) })
  }
  const onPointerEnd = () => {
    if (!from.current) return
    from.current = null
    if (drag && (Math.abs(drag.x) > 80 || drag.y < -40)) onDismiss(item.id)
    setDrag(null)
  }
  const far = drag ? Math.max(Math.abs(drag.x) / 160, -drag.y / 80) : 0

  return (
    // The row closes up as its toast leaves, so the ones below slide into the gap.
    <div
      className={cx(
        'grid transition-[grid-template-rows,margin] duration-150 ease-out',
        item.leaving ? 'mb-0 grid-rows-[0fr]' : 'mb-2 grid-rows-[1fr]',
      )}
    >
      <div className={cx('min-h-0', item.leaving && 'overflow-hidden')}>
        <div
          onPointerEnter={(e) => e.pointerType === 'mouse' && setHeld(true)}
          onPointerLeave={(e) => e.pointerType === 'mouse' && setHeld(false)}
          onFocus={() => setHeld(true)}
          onBlur={() => setHeld(false)}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          style={
            drag
              ? {
                  transform: `translate(${drag.x}px, ${drag.y}px)`,
                  opacity: Math.max(0.2, 1 - far),
                  transition: 'none',
                }
              : undefined
          }
          className={cx(
            'pointer-events-auto relative flex touch-pan-y items-start gap-2.5 overflow-hidden rounded-[14px] border border-border bg-raised py-2.5 pr-2 pl-2.5 shadow-card-hover transition-[transform,opacity] duration-150',
            item.leaving ? 'animate-toast-out' : 'animate-toast-in',
          )}
        >
          {item.sticky && (
            <span aria-hidden className={cx('absolute inset-y-0 left-0 w-[3px]', look.edge)} />
          )}
          <span
            className={cx(
              'flex size-7 shrink-0 items-center justify-center rounded-[9px]',
              look.tile,
            )}
          >
            <Icon
              className={cx('size-[17px]', item.tone === 'loading' && 'animate-spin')}
              aria-hidden
            />
          </span>
          <div className={cx('min-w-0 flex-1', !item.detail && 'pt-1')}>
            <div className="flex items-baseline gap-1.5">
              <p className="min-w-0 text-sm leading-snug font-semibold text-ink">{item.title}</p>
              {item.count > 1 && (
                <span
                  aria-label={`${item.count} times`}
                  className="shrink-0 rounded-full bg-soft px-1.5 text-[11px] font-bold text-muted"
                >
                  ×{item.count}
                </span>
              )}
            </div>
            {item.detail && (
              <p className="mt-px text-[13px] leading-snug text-muted">{item.detail}</p>
            )}
          </div>
          {item.action && (
            <button
              type="button"
              onClick={() => {
                onDismiss(item.id)
                item.action?.onClick()
              }}
              className="h-7 shrink-0 rounded-lg bg-primary-light px-2.5 text-[13px] font-bold text-primary hover:bg-primary/20"
            >
              {item.action.label}
            </button>
          )}
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => onDismiss(item.id)}
            className="flex size-7 shrink-0 items-center justify-center rounded-lg text-subtle hover:bg-soft hover:text-ink"
          >
            <X className="size-4" />
          </button>
          {/* The time left, only where there is something to press before it runs out. */}
          {life !== null && item.action && !item.leaving && (
            <span
              key={item.stamp}
              aria-hidden
              style={{
                animationDuration: `${life}ms`,
                animationPlayState: held ? 'paused' : 'running',
              }}
              className={cx(
                'absolute bottom-0 left-0 h-[3px] w-full origin-left animate-toast-bar opacity-55',
                look.bar,
              )}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext)
  if (!api) throw new Error('useToast must be used inside <ToastProvider>')
  return api
}
